import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import type { User } from '../types';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const initializingAuth = useRef(false);
  const authStateChangeTimeout = useRef<NodeJS.Timeout>();

  const fetchUserData = useCallback(async (userId: string, userEmail: string): Promise<User | null> => {
    try {
      const { data: existingUser, error: fetchError } = await supabase
        .from('users')
        .select('*')
        .eq('email', userEmail)
        .maybeSingle();

      if (fetchError && fetchError.code !== 'PGRST116') {
        throw fetchError;
      }

      if (existingUser) {
        return {
          id: existingUser.id,
          email: existingUser.email,
          first_name: existingUser.first_name || '',
          last_name: existingUser.last_name || '',
          permissions: Array.isArray(existingUser.permissions) ? existingUser.permissions : []
        };
      }

      const { data: newUser, error: insertError } = await supabase
        .from('users')
        .upsert({
          id: userId,
          email: userEmail,
          permissions: [
            'Gestion de Prix',
            'Liste des Prix',
            'Liste des Prix Boutique',
            'Gestion Des pièces sur commande',
            'Liste des pièces sur commande',
            'Nouvelle Commande',
            'Nouveau Litige',
            'Gestion Litige Assurance',
            'Gestion Litige',
            'Suivi Paiement'
          ],
          first_name: '',
          last_name: ''
        }, {
          onConflict: 'email',
          ignoreDuplicates: false
        })
        .select()
        .single();

      if (insertError) {
        throw insertError;
      }

      return {
        id: userId,
        email: userEmail,
        first_name: newUser?.first_name || '',
        last_name: newUser?.last_name || '',
        permissions: Array.isArray(newUser?.permissions) ? newUser.permissions : []
      };
    } catch (error) {
      console.error('Error in fetchUserData:', error);
      return null;
    }
  }, []);

  const handleAuthStateChange = useCallback(async (event: string, session: any) => {
    // Clear any pending timeout
    if (authStateChangeTimeout.current) {
      clearTimeout(authStateChangeTimeout.current);
    }

    // Set a new timeout to handle the auth state change
    authStateChangeTimeout.current = setTimeout(async () => {
      try {
        if (event === 'SIGNED_OUT' || !session?.user) {
          setUser(null);
          setLoading(false);
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
          return;
        }

        const userData = await fetchUserData(session.user.id, session.user.email!);
        setUser(userData);
      } catch (error) {
        console.error('Error in auth state change handler:', error);
        setUser(null);
      } finally {
        setLoading(false);
      }
    }, 100); // Small delay to prevent rapid state changes
  }, [fetchUserData]);

  useEffect(() => {
    const initializeAuth = async () => {
      // Prevent multiple initializations
      if (initializingAuth.current) return;
      initializingAuth.current = true;

      try {
        const { data: { session } } = await supabase.auth.getSession();
        await handleAuthStateChange(session ? 'SIGNED_IN' : 'SIGNED_OUT', session);
      } catch (error) {
        console.error('Error in initializeAuth:', error);
        setUser(null);
        setLoading(false);
      } finally {
        initializingAuth.current = false;
      }
    };

    initializeAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(handleAuthStateChange);

    return () => {
      if (authStateChangeTimeout.current) {
        clearTimeout(authStateChangeTimeout.current);
      }
      subscription.unsubscribe();
    };
  }, [handleAuthStateChange]);

  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true);
      const { data: { session }, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        if (error.message === 'Invalid login credentials') {
          throw new Error('Email ou mot de passe incorrect');
        }
        throw error;
      }

      if (!session?.user) {
        throw new Error('Erreur de connexion: aucun utilisateur trouvé');
      }

      const userData = await fetchUserData(session.user.id, session.user.email!);
      if (!userData) {
        throw new Error('Erreur lors de la récupération des données utilisateur');
      }
      setUser(userData);
    } catch (error: any) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);
      await supabase.auth.signOut();
      setUser(null);
      window.location.href = '/login';
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}